
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action, computed } from "mobx";

// MARK: Components
import { IRowItem, ColumnType } from "../../components/Table/TableRow";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore, routerStore, languageStore } from "../_rootStore";
import ImageService from "../../services/ImageService";

// MARK: Resources
import strings from "../../resources/strings";

export default class GastroPlaceStore extends VariableChangeHandler {
	// Services
	public imageService: ImageService = new ImageService();

	// Control
	@observable public loading: boolean = false;
	@observable public onEditMode: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public gastroPlaces: api.GastroPlace[] = [];
	@observable public selectedGastroPlace: api.GastroPlace | null = null;

	// Variables
	@observable public name: string = "";
	@observable public nick: string = "";
	@observable public description: string = "";
	@observable public url: string = "";

	@observable public gastroPlaceTypes: api.GastroPlaceType[] = [];
	@observable public selectedGastroPlaceType: api.GastroPlaceType | null = null;

	@computed
	public get header() {
		const header = strings.gastroPlaces.table.header;
		return [
			header.type,
			header.picture,
			header.nick,
			header.name,
			header.description,
		];
	}

	private static formatGastroPlace(gastroPlace: api.GastroPlace): IRowItem {
		const gastroPlaceInSelectedLanguage = gastroPlace.languages.filter((gastroPlaceLanguage) =>
		gastroPlaceLanguage.language === languageStore.selectedLanguage)[0].translation;

		return {
			id: gastroPlace.id,
			name: gastroPlaceInSelectedLanguage.name,
			columns: [
				{
					value: api.translateGastroPlaceType(gastroPlaceInSelectedLanguage.type),
				},
				{
					value: gastroPlaceInSelectedLanguage.images[0].url,
					type: ColumnType.avatar,
				},
				{
					value: gastroPlaceInSelectedLanguage.nick,
				},
				{
					value: gastroPlaceInSelectedLanguage.name,
				},
				{
					value: gastroPlaceInSelectedLanguage.description,
				},
			],
		};
	}

	@computed
	public get gastroPlacesTableRows(): IRowItem[] {
		return this.gastroPlaces.map(GastroPlaceStore.formatGastroPlace);
	}

	@action
	public getGastroPlace = async (gastroPlaceId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			this.selectedGastroPlace = await api.getGastroPlace(gastroPlaceId);
			this.setEditorFields(this.selectedGastroPlace);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public getGastroPlaces = async (pageOffSet?: number) => {
		if (this.loading) {
			return;
		}

		if (!pageOffSet) {
			pageOffSet = 0;
		}

		this.loading = true;

		try {
			this.gastroPlaces = await api.getGastroPlaces(pageOffSet);
			this.pageOffSet = pageOffSet;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public createOrEditGastroPlace = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			if (!this.selectedGastroPlace) {
				const newGastroPlace: api.NewGastroPlace = {
					name: this.name,
					nick: this.nick,
					description: this.description,
					url: this.url ? this.url : null,
					type: this.selectedGastroPlaceType ? this.selectedGastroPlaceType : api.GastroPlaceType.bar,
					images: this.imageService.getUncertainfiedImages(),
				};
				this.selectedGastroPlace = await api.createGastroPlace(newGastroPlace);
			} else {
				const editedGastroPlace: api.EditGastroPlace = {
					language: languageStore.selectedLanguage,
					nick: this.nick,
					name: this.name,
					description: this.description,
					url: this.url,
					type: this.selectedGastroPlaceType ? this.selectedGastroPlaceType : api.GastroPlaceType.bar,
					images: this.imageService.getUncertainfiedImages(),
				};
				this.selectedGastroPlace = await api.editGastroPlace(this.selectedGastroPlace.id, editedGastroPlace);
			}
			routerStore.replace("/dashboard/gastroPlaces");
			window.location.reload();
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public deleteGastroPlace = async (gastroPlaceId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			await api.deleteGastroPlace(gastroPlaceId);
			this.gastroPlaces = this.gastroPlaces.filter((gastroPlace) => gastroPlace.id !== gastroPlaceId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public openDeleteGastroPlaceDialog = async (gastroPlaceId: string, gastroPlaceName: string) => {
		uiStore.showDialog({
			title: strings.gastroPlaces.deleteGastroPlaceDialog.title,
			message: `${strings.gastroPlaces.deleteGastroPlaceDialog.message}: ${gastroPlaceName}`,
		}, () => this.deleteGastroPlace(gastroPlaceId));
	};

	@action
	public nextPage = async () => {
		await this.getGastroPlaces(this.pageOffSet + 1);
	};

	@action
	public previousPage = async () => {
		if (this.pageOffSet > 0) {
			await this.getGastroPlaces(this.pageOffSet - 1);
		}
	};

	@action
	public clear = () => {
		this.imageService.clear();
		this.name = "";
		this.nick = "";
		this.description = "";
		this.url = "";
	};

	@action
	public setEditorFields = (gastroPlace: api.GastroPlace) => {
		const gastroPlaceInSelectedLanguage = gastroPlace.languages.filter((gastroPlaceLanguage) =>
		gastroPlaceLanguage.language === languageStore.selectedLanguage)[0].translation;

		this.imageService.setImages(gastroPlaceInSelectedLanguage.images);
		this.name = gastroPlaceInSelectedLanguage.name;
		this.nick = gastroPlaceInSelectedLanguage.nick;
		this.description = gastroPlaceInSelectedLanguage.description;
		this.url = gastroPlaceInSelectedLanguage.url ? gastroPlaceInSelectedLanguage.url : "";
		this.selectedGastroPlaceType = gastroPlaceInSelectedLanguage.type;
	};

	@action
	public startGastroPlaceCreateOrEdit(gastroPlaceId?: string) {
		if (gastroPlaceId) {
			this.getGastroPlace(gastroPlaceId);
			routerStore.push(`/dashboard/gastroPlaces/editor/${gastroPlaceId}`);
		} else {
			this.selectedGastroPlace = null;
			this.clear();
			routerStore.push("/dashboard/gastroPlaces/editor/");
		}
	}

	@action
	public getGastroPlaceTypes = () => {
		if (this.loading)  {
			return;
		}

		this.loading = true;

		try {
			this.gastroPlaceTypes = api.allValuesGastroPlaceType();
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public toggleGastroPlaceTypes = (gastroPlaceType: api.GastroPlaceType) => {
		this.selectedGastroPlaceType = gastroPlaceType;
	};
}
